import React, { forwardRef, memo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Tooltip from "../tooltip";
import { IProps } from "./markdown.types";
import { preprocessString, preserveNewlines } from "./markdown.utilities";
import classNames from "classnames";

// For the string "issueStart[test-classname,test-description]test-textissueEnd", this regex will return three groups:
// "test-classname", "test-description" and "test-text".
const STRONG_CLASSNAME_REGEX = /issueStart\[(.*),(.*)\]([\s\S]*)issueEnd/;

/**
 * Render markdown. This is just a wrapper to provide styles.
 * @param props.linkTarget     Any links will receive this as their "target" attribute.
 * @param props.markdownString Text to convert to markdown.
 * @param ref                  Optional forward ref.
 */
const Markdown = forwardRef<HTMLDivElement, IProps>(({ linkTarget = "_blank", markdownString, fullHeight }, ref) => (
    <div className={classNames("markdown", { "markdown--full-height": fullHeight })} ref={ref}>
        <ReactMarkdown
            components={{
                a: ({ node, ...props }) => {
                    const { children, ...otherProps } = props;
                    return (
                        <a
                            {...otherProps}
                            target={linkTarget}
                            rel={linkTarget === "_blank" ? "noreferrer noopener" : undefined}
                        >
                            {children}
                            {linkTarget === "_blank" && (
                                <span className="markdown__screen-reader-text"> this link will open in a new tab</span>
                            )}
                        </a>
                    );
                },
                strong: ({ children }) => {
                    // Allow a class name and description with the syntax:
                    // "**issueStart[className,description]textissueEnd**".
                    const textContent = React.Children.toArray(children).join("");
                    const match = textContent.match(STRONG_CLASSNAME_REGEX);

                    if (match) {
                        return (
                            <Tooltip content={<div className="markdown__writing-issue-tooltip">{match[2]}</div>}>
                                <strong className={match[1]} role="strong">
                                    {match[3]}
                                </strong>
                            </Tooltip>
                        );
                    }

                    return <strong role="strong">{children}</strong>;
                },
            }}
            remarkPlugins={[remarkGfm]}
        >
            {preserveNewlines(preprocessString(markdownString))}
        </ReactMarkdown>
    </div>
));

export default memo(Markdown);
